// @flow

import React, { Component } from "react";
import { NavLink, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import cx from "classnames";
import { get, orderBy, isEqual } from "lodash";
import { localStorage } from "utils/storage";
import { getExtUrlLang, getLang } from "utils/language";
import Modal from "react-responsive-modal";
import { getClassName } from "ui-kit/utils/component";
import { Page } from "components";
import { ACCESS_TOKEN } from "utils/http";
import { Button } from "ui-kit/components";
import {
  getDashboard,
  getTickers,
  getTransactions,
  setMarketingEmailConsentInSettings,
  goToInvestPage
} from "ducks/user/actions";

import Card from "./card/Card";
import TickersChart from "./tickersChart/TickersChart";
import AccountHistory from "./accountHistory/AccountHistory";

import "./Dashboard.scss";
import DateHelper from '../../utils/date';

export const ccn = getClassName("Dashboard");

type StateProps = {
  authorized: boolean
};

class Dashboard extends Component {
  state = {
    modal: false,
    modalHasShown: false
  };

  componentDidMount() {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    if (accessToken) {
      this.props.getDashboard();
      this.props.getTickers();
      this.props.getTransactions();
    }
  }

  componentWillReceiveProps(next) {
    const { modalHasShown } = this.state;
    const isEU = !get(next, "user.settings.infoGDPR", true);

    if (!modalHasShown && isEU && get(next, "user.settings.consent") === false) {
      // make popup with switch button for the first time
      this.setState({
        modal: !localStorage.getItem("MARKETING_EMAIL_SHOWED_ONCE"),
        modalHasShown: true
      });
    }
  }

  checkBox = (e) => {
    this.props.setMarketingEmailConsentInSettings(e.target.checked);
    if (e.target.checked) {
      setTimeout(() => this.closeModal(), 1000);
    }
  };

  closeModal = () => {
    this.setState(prev => ({ modal: !prev.modal }));
  };

  goToInvestPage = () => {
    this.props.goToInvestPage();
  };

  renderNotice() {
    const { user, lang } = this.props;

    if (user.upholdStatus === "ok" && user.memberAt === null) {
      return (
        <div className={ccn("warning")}>
          <FormattedMessage id="hbc.dashboard.pending.warning" defaultMessage="Please complete the setup of your " />
          <a href={`https://uphold.com/${getExtUrlLang(lang)}/hedged-bitcoin`}>
            <FormattedMessage id="hbc.dashboard.pending.warning.uphold" defaultMessage="Uphold account" /> >
          </a>
        </div>
      );
    }

    if (user.upholdStatus === "ok" && !user.hasBTC) {
      return (
        <div className={ccn("warning")}>
          <FormattedMessage
            id="hbc.dashboard.ok.warning"
            defaultMessage="Sorry, but your Uphold account does not support Bitcoin. Please contact "
          />
          <a href="mailto:support@uphold.com">
            <FormattedMessage id="hbc.dashboard.ok.warning.uphold.support" defaultMessage="Uphold Support" />
          </a>
        </div>
      );
    }

    if (user.upholdStatus === "pending") {
      return (
        <div className={ccn("warning")}>
          <FormattedMessage id="hbc.dashboard.pending.warning" defaultMessage="Please complete the setup of your " />
          <a href={`https://uphold.com/${getExtUrlLang(lang)}/hedged-bitcoin`}>
            <FormattedMessage id="hbc.dashboard.pending.warning.uphold" defaultMessage="Uphold account" /> >
          </a>
        </div>
      );
    }

    if (user.upholdStatus === "restricted") {
      return (
        <div className={ccn("warning")}>
          <FormattedMessage
            id="hbc.dashboard.restricted.warning"
            defaultMessage="Your Uphold account is subject to limitations, please check "
          />
          <a href="https://uphold.com">
            <FormattedMessage id="hbc.dashboard.restricted.warning.your.account" defaultMessage="your account" />
          </a>
        </div>
      );
    }

    if (user.upholdStatus === "blocked") {
      return (
        <div className={ccn("warning")}>
          <FormattedMessage
            id="hbc.dashboard.blocked.warning"
            defaultMessage="Your Uphold account is restricted, please contact "
          />
          <a href="mailto:support@uphold.com">
            <FormattedMessage id="hbc.dashboard.ok.warning.uphold.support" defaultMessage="Uphold Support" />
          </a>
        </div>
      );
    }

    return null;
  }

  render() {
    const { user, tickers, transactions, intl: { formatMessage } } = this.props;
    const { modal } = this.state;

    const isMoreThenOneCard = Boolean(get(user, "invest") || get(user, "fid") || get(user, "fcd"));

    if (modal && !localStorage.getItem("MARKETING_EMAIL_SHOWED_ONCE")) {
      localStorage.setItem("MARKETING_EMAIL_SHOWED_ONCE", true);
    }

    return (
      <Page>
        <div className={ccn("container")}>
          <h1 className={ccn("title")}>
            <FormattedMessage id="hbc.dashboard.title" defaultMessage="Dashboard" />
          </h1>
          <div className={cx(ccn("row"), ccn("row_head"))}>
            <div className={cx(ccn("column"), ccn("column_alert"))}>
              {user && this.renderNotice()}
            </div>
            { isMoreThenOneCard && (
              <NavLink className={cx(ccn("column"), "CardButton")} to={`/${getLang()}/invest`}>
                <Button className="Button--invest">
                  <FormattedMessage id="hbc.dashboard.invest" defaultMessage="invest" />
                </Button>
              </NavLink>
            )}
          </div>
          <div className={cx(ccn("row"), ccn("row_balance"), { [ccn("row_balance-one-card")]: !isMoreThenOneCard })}>
            {user && <Card
              userInvest={user.invest}
              userBalance={user.balance}
              userFid={user.fid}
              userFcd={user.fcd}
            />}
          </div>
          <div className={cx(ccn("row"), ccn("row_account"))}>
            <AccountHistory
              data={transactions}
            />

            <TickersChart
              tickers={orderBy(tickers, ["timestamp"], ["asc"])}
              userBalance={user && user.balance}
            />

            {get(user, "balance") && (
              <React.Fragment>
                <p className={ccn("balance-estimate plus-six")}>*
                  {formatMessage({
                    id: "hbc.dashboard.balance.estimate",
                    defaultMessage: "Balance estimate last updated at {currentDate}"
                  }, {
                    currentDate: get(user, "balance.date") && new DateHelper(user.balance.date).format("dd/mm/yyyy")
                  })}.
                </p>
                <p className={ccn("balance-estimate plus-seven")}>
                  <FormattedMessage
                    id="hbc.dashboard.balance.estimates.see"
                    defaultMessage="Balance estimates may be different from the actual value upon withdrawal; please see "
                  />
                  <a href="https://support.hedgedbitcoin.com/hc/en-us/articles/360018818751">
                    <FormattedMessage id="hbc.withdraw.added.to.waitlist.two.tos" defaultMessage="Terms of Service" />
                  </a>
                  <FormattedMessage id="hbc.withdraw.added.to.waitlist.two.and" defaultMessage=" and " />
                  <a href="https://support.hedgedbitcoin.com/hc/en-us/sections/360002645552-Deposits-and-Withdrawals">
                    <FormattedMessage id="hbc.withdraw.added.to.waitlist.two.faq" defaultMessage="FAQ" />
                  </a>
                  <FormattedMessage id="hbc.dashboard.for.more.information" defaultMessage=" for more information" />.
                </p>
              </React.Fragment>
            )}
          </div>
        </div>

        <Modal classNames={{ overlay: ccn("modal"), modal: ccn("modal-body") }} open={modal} onClose={this.closeModal}>
          {
            modal && (
              <React.Fragment>
                <React.Fragment>
                  <span className="close big" />
                  <div className="form-check checkbox-slider--b-flat">
                    <label htmlFor="mail_agree">
                      <input id="mail_agree" type="checkbox" onChange={this.checkBox} />
                      <span>
                        <p>
                          <FormattedMessage
                            id="hbc.settings"
                            defaultMessage="Yes, please email me updates, tips, and offers related to this product"
                          />
                        </p>
                      </span>
                    </label>
                  </div>
                </React.Fragment>
              </React.Fragment>
            )
          }
        </Modal>
      </Page>
    );
  }
}

const mapStateToProps = ({ user }): StateProps => ({
  user:         user.info,
  lang:         user.lang,
  tickers:      user.tickers,
  transactions: user.transactions,
});

export default withRouter(connect(mapStateToProps, {
  getDashboard,
  getTickers,
  getTransactions,
  setMarketingEmailConsentInSettings,
  goToInvestPage,
})(injectIntl(Dashboard)));



// WEBPACK FOOTER //
// ./src/containers/dashboard/Dashboard.js